import { css } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import type { EnvironmentResource } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import ChangedValueIndicator from "~/areas/projects/components/Variables/TenantVariables/ChangedValueIndicator";
import { EnvironmentChip } from "~/components/Chips";
import { EnvironmentMultiSelect } from "~/components/MultiSelect/EnvironmentMultiSelect";
import PopoverWhenFocused from "~/components/PopoverWhenFocused";
import type { PopoverPosition } from "~/components/PopoverWhenFocused/PopoverWhenFocused";
import { Text } from "~/components/form/index";
import type { RowData } from "./ScopedTenantTemplateDataGrid";
export interface TenantTemplateScopeCellProps {
    item: RowData;
    enabled: boolean;
    canBeScoped: boolean;
    isFocused: boolean;
    onFocus: () => void;
    onBlur: () => void;
    onChange: (environmentIds: string[]) => void;
    environments: EnvironmentResource[];
}
export function TenantTemplateScopeCell(props: TenantTemplateScopeCellProps) {
    const { item, enabled, isFocused, onFocus, onBlur, onChange, environments, canBeScoped } = props;
    const calculateFocusedPopoverPosition = (): PopoverPosition => ({ top: 0, left: 0, right: 0 });
    return enabled ? (<div onFocus={onFocus} onClick={onFocus} className={cellStyles.editableContainer}>
            <PopoverWhenFocused onClickOutside={onBlur} isFocused={isFocused} dynamicPosition={calculateFocusedPopoverPosition}>
                {isFocused ? (<div className={cellStyles.focusedContainer}>
                        <EnvironmentMultiSelect value={item.EnvironmentIds} environments={environments} onChange={(environmentIds) => onChange(environmentIds)}/>
                    </div>) : (<div className={cellStyles.readonlyContainer}>
                        <ReadonlyScopeCell row={item} environments={environments} canBeScoped={canBeScoped}/>
                        <div tabIndex={0} onFocus={onFocus}/>
                        <ChangedValueIndicator show={item.HasScopeChanged}/>
                    </div>)}
            </PopoverWhenFocused>
        </div>) : (<div className={cellStyles.editableContainer} style={{ cursor: "default" }}>
            <div className={cellStyles.readonlyContainer}>
                <ReadonlyScopeCell row={item} environments={environments} canBeScoped={canBeScoped}/>
            </div>
        </div>);
}
interface ReadonlyScopeCellProps {
    row: RowData;
    environments: EnvironmentResource[];
    canBeScoped: boolean;
}
function ReadonlyScopeCell({ row, environments, canBeScoped }: ReadonlyScopeCellProps) {
    if (row.EnvironmentIds.length === 0) {
        return <Text value={""} placeholder={canBeScoped ? "Define Scope" : undefined} onChange={noOp} hideUnderline={true} showValueAsTitleAttribute={true} monoSpacedFont={true} disabled={!canBeScoped}/>;
    }
    return (<div>
            {row.EnvironmentIds.map((id) => (<EnvironmentChip key={id} environmentName={environments.find((e) => e.Id === id)?.Name ?? id}/>))}
        </div>);
}
const cellStyles = {
    editableContainer: css({
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "4rem",
    }),
    focusedContainer: css({
        padding: space[8],
    }),
    readonlyContainer: css({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: space[8],
        width: "100%",
        height: "100%",
        padding: `${space[8]} ${space[12]}`,
    }),
};
